import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { customerApi } from "@/customer/api";
import { useCustomerAuth } from "@/customer/AuthContext";

const Ctx = createContext({
  cart: null,
  count: 0,
  loading: false,
  refresh: async () => {},
  addItems: async () => {},
  removeItem: async () => {},
  clear: async () => {},
});

export const CartProvider = ({ children }) => {
  const { user } = useCustomerAuth();
  const userId = user?.id || null;
  const [cartByUser, setCartByUser] = useState({});
  const [loading, setLoading] = useState(false);

  const cart = userId ? cartByUser[userId] || null : null;

  const refresh = useCallback(async () => {
    if (!userId) return null;
    setLoading(true);
    try {
      const r = await customerApi.get("/customer/cart");
      setCartByUser((prev) => ({ ...prev, [userId]: r.data }));
      return r.data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) refresh();
  }, [userId, refresh]);

  const addItems = useCallback(async (photoIds) => {
    if (!userId) throw new Error("not_authenticated");
    const r = await customerApi.post("/customer/cart/items", { photo_ids: photoIds });
    await refresh();
    return r.data;
  }, [userId, refresh]);

  const removeItem = useCallback(async (photoId) => {
    if (!userId) return;
    await customerApi.delete(`/customer/cart/items/${photoId}`);
    await refresh();
  }, [userId, refresh]);

  const clear = useCallback(async () => {
    if (!userId) return;
    await customerApi.delete("/customer/cart");
    await refresh();
  }, [userId, refresh]);

  const value = useMemo(() => ({
    cart,
    count: cart?.selected_count || 0,
    loading,
    refresh,
    addItems,
    removeItem,
    clear,
  }), [cart, loading, refresh, addItems, removeItem, clear]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCart = () => useContext(Ctx);

